import { CHART } from '@/lib/chartColors';
// src/pages/team/TeamUsagePage.tsx
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { formatCompact, formatNumber } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { ChartTooltip } from '@/components/charts/ChartTooltip';

const shortDate = (iso: unknown) => typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso)
  ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  : String(iso);

const TeamUsagePage: React.FC = () => {
  const { data: breakdown = [], isLoading } = useQuery({
    queryKey: ['team-usage-breakdown'],
    queryFn: () =>
      apiClient.get('/analytics/team-breakdown').then((r: any) => r.data?.data ?? []),
  });

  // Aggregate by date for chart
  const byDate = breakdown.reduce((acc: any, row: any) => {
    const key = row.date;
    acc[key] = (acc[key] ?? 0) + row.credits;
    return acc;
  }, {});
  const chartData = Object.entries(byDate).map(([date, credits]) => ({ date, credits })).slice(-14);

  return (
    <div className="space-y-6">
      <PageHeader title="Team usage" description="Credits your team used, per day and per member." />

      <Panel title="Credits used, last 14 days">
        {isLoading ? (
          <div className="skeleton h-56" aria-hidden="true" />
        ) : chartData.length ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 0, bottom: 0, left: -8 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={shortDate} tickLine={false} axisLine={false} minTickGap={16} />
                <YAxis tickFormatter={formatCompact} tickLine={false} axisLine={false} width={48} />
                <Tooltip content={<ChartTooltip />} labelFormatter={shortDate} cursor={{ fill: 'rgb(var(--color-surface-raised))' }} />
                <Bar dataKey="credits" name="Credits" fill={CHART.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState icon={<BarChart3 />} title="No usage yet." description="Credits your team uses show up here within a minute." className="min-h-56 rounded-xl border border-dashed border-border" />
        )}
      </Panel>

      <Panel title="Per-member breakdown" bodyClassName="px-0 pb-2">
        {isLoading ? (
          <TableSkeleton rows={4} />
        ) : breakdown.length ? (
          <Table minWidth={520}>
            <THead>
              <tr>
                <Th>User</Th>
                <Th>Service</Th>
                <Th align="right">Requests</Th>
                <Th align="right">Credits</Th>
              </tr>
            </THead>
            <TBody>
              {breakdown.map((row: any, i: number) => (
                <Tr key={i}>
                  <Td mono muted title={row.user_id}>{row.user_id.slice(0, 8)}…</Td>
                  <Td>{row.service}</Td>
                  <Td align="right" mono>{formatNumber(Number(row.requests))}</Td>
                  <Td align="right" mono>{formatNumber(Number(row.credits))}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ) : (
          <p className="px-5 pb-4 pt-2 text-sm text-muted-foreground">No requests from team members yet.</p>
        )}
      </Panel>
    </div>
  );
};

export default TeamUsagePage;
